import { useEffect, useRef, useState } from 'react';
import { Dialog } from '../dialogs/dialog';
import { ExplainedButton as Button } from '../explained-button';
import { Rating } from 'primereact/rating';
import { AdminCertificateDto, AdminReviewDto } from '../../general/dtos';
import { refreshAdminTaskCount } from '../../general/admin-tasks';
import { useFullscreenDialogParent } from '../../general/use-fullscreen-dialog-parent';
import { useApi } from '../../providers/api-provider';

export function UserCourseReviewDialog({ id, onClose }: { id: number; onClose: () => void }) {
    const api = useApi();
    const parent = useFullscreenDialogParent();
    const [review, setReview] = useState<AdminReviewDto | null>(null);
    const [error, setError] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [saving, setSaving] = useState(false);
    const [notice, setNotice] = useState('');
    const pending = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        const abort = new AbortController();
        setReview(null); setError('');
        api?.getAdminReview(String(id), abort.signal).then(result => {
            if (!abort.signal.aborted) setReview(result);
        }).catch(failure => {
            if (!abort.signal.aborted) setError(failure?.response?.data?.message || 'Could not load this review. Please try again.');
        });
        return () => abort.abort();
    }, [api, id, attempt]);
    const moderate = async (approved: boolean) => {
        if (!api || !review?.share_with_others || pending.current || approved === review.approved_by_admin) return;
        pending.current = true; setSaving(true); setError(''); setNotice('');
        try {
            if (approved) await api.approveReview(id); else await api.unapproveReview(id);
            refreshAdminTaskCount();
            if (mounted.current) {
                setReview(previous => previous ? { ...previous, approved_by_admin: approved } : previous);
                setNotice(approved ? 'Review approved for public display.' : 'Review disapproved. It is no longer displayed publicly.');
            }
        } catch (failure: any) {
            if (mounted.current) setError(failure?.response?.data?.message || 'Could not change review approval. Please try again.');
        } finally { pending.current = false; if (mounted.current) setSaving(false); }
    };
    return <Dialog header="Course review" visible modal appendTo={parent} draggable={false}
        style={{ width: '38rem', maxWidth: '95vw' }} closable={!saving} closeOnEscape={!saving} onHide={() => { if (!pending.current) onClose(); }}
        footer={<div className="flex justify-content-end flex-wrap gap-2">
            {review?.share_with_others && <>
                <Button label="Approve" icon="pi pi-check" disabled={saving || review.approved_by_admin}
                    disabledReason={saving ? 'Wait for the review update to finish.' : 'This review is already approved.'} onClick={() => void moderate(true)} />
                <Button label="Disapprove" icon="pi pi-times" severity="warning" outlined disabled={saving || !review.approved_by_admin}
                    disabledReason={saving ? 'Wait for the review update to finish.' : 'This review is not approved for public display.'} onClick={() => void moderate(false)} />
            </>}
            <Button label="Close" text disabled={saving} disabledReason="Wait for the update to finish before closing." onClick={onClose} />
        </div>}>
        {!review && !error && <p role="status">Loading review...</p>}
        {error && <p role="alert">{error} {!review && <Button label="Retry review" text onClick={() => setAttempt(value => value + 1)} />}</p>}
        {review && <div className="flex flex-column gap-3">
            <h3 className="m-0">{review.course_name}</h3>
            <div>{review.reviewer_name || 'Learner'}{review.reviewer_email ? ` (${review.reviewer_email})` : ''}</div>
            <Rating value={review.stars} readOnly cancel={false} aria-label={`${review.stars} out of 5 stars`} />
            <time dateTime={review.created_at}>{new Date(review.created_at).toLocaleString()}</time>
            <blockquote className="m-0 p-3 surface-ground border-round" style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>{review.text}</blockquote>
            <p className="m-0">{review.share_with_others ? `Public sharing allowed. ${review.approved_by_admin ? 'Approved' : 'Not approved'} for public display.`
                : 'Private feedback. The learner has not allowed this review to be shared publicly.'}</p>
            {notice && <p role="status" className="m-0">{notice}</p>}
        </div>}
    </Dialog>;
}

export function UserCourseCertificateDialog({ certificateKey, onClose }: { certificateKey: string; onClose: () => void }) {
    const api = useApi();
    const parent = useFullscreenDialogParent();
    const [certificate, setCertificate] = useState<AdminCertificateDto | null>(null);
    const [error, setError] = useState('');
    const [previewError, setPreviewError] = useState('');
    const [preview, setPreview] = useState('');
    const [attempt, setAttempt] = useState(0);
    const [previewAttempt, setPreviewAttempt] = useState(0);
    const [saving, setSaving] = useState(false);
    const pending = useRef(false);
    const mounted = useRef(true);
    useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
    useEffect(() => {
        const abort = new AbortController();
        setCertificate(null); setError('');
        api?.getAdminCertificate(certificateKey, abort.signal).then(result => {
            if (!abort.signal.aborted) setCertificate(result);
        }).catch(failure => {
            if (!abort.signal.aborted) setError(failure?.response?.data?.message || 'Could not load this certificate. Please try again.');
        });
        return () => abort.abort();
    }, [api, certificateKey, attempt]);
    const name = certificate?.recipient_name;
    useEffect(() => {
        const abort = new AbortController();
        let url = '';
        setPreview(''); setPreviewError('');
        if (name != null) api?.getAdminCertificatePreview(certificateKey, abort.signal).then(blob => {
            if (abort.signal.aborted) return;
            url = URL.createObjectURL(blob); setPreview(url);
        }).catch(() => { if (!abort.signal.aborted) setPreviewError('Could not generate the certificate preview. Please try again.'); });
        return () => { abort.abort(); if (url) URL.revokeObjectURL(url); };
    }, [api, certificateKey, name, previewAttempt]);
    const changeStatus = async (revoked: boolean) => {
        if (!api || !certificate || pending.current || Boolean(certificate.revoked_at) === revoked) return;
        pending.current = true; setSaving(true); setError('');
        try {
            const updated = await api.setCertificateRevoked(certificateKey, revoked);
            if (mounted.current) {
                setCertificate(updated);
            }
        } catch (failure: any) {
            if (mounted.current) setError(failure?.response?.data?.message || 'Could not change certificate status. Please try again.');
        } finally { pending.current = false; if (mounted.current) setSaving(false); }
    };
    return <Dialog header="Course certificate" visible modal appendTo={parent} draggable={false}
        style={{ width: '75rem', maxWidth: '95vw' }} closable={!saving} closeOnEscape={!saving} onHide={() => { if (!pending.current) onClose(); }}
        footer={<div className="flex justify-content-end flex-wrap gap-2">
            {certificate && <>
                <Button label="Grant" icon="pi pi-check" disabled={saving || !certificate.revoked_at}
                    disabledReason={saving ? 'Wait for the certificate update to finish.' : 'This certificate is already granted.'} onClick={() => void changeStatus(false)} />
                <Button label="Revoke" icon="pi pi-ban" severity="danger" outlined disabled={saving || !!certificate.revoked_at}
                    disabledReason={saving ? 'Wait for the certificate update to finish.' : 'This certificate is already revoked.'} onClick={() => void changeStatus(true)} />
            </>}
            <Button label="Close" text disabled={saving} disabledReason="Wait for the update to finish before closing." onClick={onClose} />
        </div>}>
        {!certificate && !error && <p role="status">Loading certificate...</p>}
        {error && <p role="alert">{error} {!certificate && <Button label="Retry certificate" text onClick={() => setAttempt(value => value + 1)} />}</p>}
        {certificate && <>
            {name === null && <p role="status">Awaiting the learner’s certificate name confirmation.</p>}
            {name != null && !preview && !previewError && <p role="status">Generating preview...</p>}
            {previewError && <p role="alert">{previewError} <Button label="Retry preview" text onClick={() => setPreviewAttempt(value => value + 1)} /></p>}
            {preview && <img src={preview} alt={`Certificate for ${certificate.recipient_name}`} style={{ display: 'block', width: '100%', maxHeight: '65vh', objectFit: 'contain' }}
                onError={() => { setPreview(''); setPreviewError('Could not display the certificate preview.'); }} />}
        </>}
    </Dialog>;
}
